// ── Shared constants, metadata, and pure helpers ──
// All threshold logic lives exclusively in the FastAPI backend.
// UI is a pure consumer of backend-supplied level keys.

import { alpha } from '@mui/material/styles';
import {
  TrendingUp, RocketLaunch, Warning, HighlightOff,
  OpenInFull, Diamond, Bolt, GpsFixed,
  CheckCircle, InfoOutlined, ReportProblem,
} from '@mui/icons-material';
import { colors, fontSize, sxPresets, status } from '../../theme';

export const fmtDec = (n, d = 1) => n != null ? Number(n).toFixed(d) : '—';

export const thSx = {
  ...sxPresets.tableHead,
  letterSpacing: '0.04em', textTransform: 'uppercase', px: 1.5,
  borderBottom: `2px solid ${colors.gray[200]}`,
  whiteSpace: 'nowrap', lineHeight: 1.4,
};
export const tdSx = {
  py: 0.7, px: 1.5, fontSize: fontSize.md,
  borderBottom: `1px solid ${colors.gray[100]}`,
};
export const trSx = () => ({
  '&:nth-of-type(even)': { bgcolor: colors.gray[50] },
  '&:hover': {
    bgcolor: alpha(colors.info.main, 0.05),
    '& td:first-of-type': { borderLeft: `2px solid ${colors.info.main}` },
  },
  transition: 'all 0.15s ease',
});

// Color maps for backend-supplied level keys (UI owns presentation only).
export const PILLAR_LEVEL_COLOR = {
  strong:  colors.success.main,
  good:    colors.info.main,
  average: colors.warning.main,
  weak:    colors.error.main,
};
export const RISK_LEVEL_COLOR = {
  low:      colors.success.main,
  medium:   colors.info.main,
  high:     colors.warning.main,
  veryHigh: colors.error.main,
};

export const scoreColor = (levelKey) => PILLAR_LEVEL_COLOR[levelKey] || colors.gray[400];
export const riskColor  = (levelKey) => RISK_LEVEL_COLOR[levelKey]   || colors.gray[400];

export const tierMeta = {
  LEADER:   { color: colors.success.main, labelKey: 'leader',   icon: '★' },
  EMERGING: { color: colors.info.main,    labelKey: 'emerging',  icon: '↑' },
  WATCH:    { color: colors.warning.main, labelKey: 'watch',     icon: '◎' },
  REST:     { color: colors.gray[400],    labelKey: 'rest',      icon: '' },
};
export const getTier = (t) => tierMeta[t] || tierMeta.REST;

// Trade-style metadata — display + routing only. Text (short/tip) is i18n'd at render time.
export const tradeStyleMeta = {
  WATCHLIST:         { color: colors.info.main,             Icon: GpsFixed,     label: 'WATCHLIST',      short: 'Sẵn sàng vào lệnh',    bucket: 'buy',    tip: 'Cấu trúc trend còn nguyên, đã chỉnh nhẹ và sẵn sàng phá nền.' },
  EARLY_BREAKOUT:    { color: colors.accent.green,  Icon: RocketLaunch, label: 'EARLY_BREAKOUT', short: 'Mới chớm bứt phá',     bucket: 'watch',  tip: 'Điểm leader-quality (score≥75) ở giai đoạn trend mới chớm.' },
  LONG_TREND_HOLD:   { color: colors.accent.cyan,   Icon: Diamond,      label: 'LONG_TREND',     short: 'Trend dài hạn còn tốt', bucket: 'review', tip: 'Trend nhiều tuần/tháng còn nguyên.' },
  TPLUS_MOMENTUM:    { color: colors.accent.violet, Icon: Bolt,         label: 'T+',             short: 'Lướt sóng ngắn hạn',   bucket: 'watch',  tip: 'Volume bùng nổ + giá xác nhận. Lướt T+3 đến T+5.' },
  SWING_TREND_SETUP: { color: colors.accent.sky,    Icon: TrendingUp,   label: 'SWING',          short: 'Setup swing trung hạn', bucket: 'watch',  tip: 'Đáy cao hơn đáy trước, MA20 dốc lên.' },
  OVEREXTENDED_RISK: { color: colors.warning.main,  Icon: OpenInFull,   label: 'OVEREXT',        short: 'Quá xa MA, dễ chỉnh',  bucket: 'review', tip: 'Giá xa MA20 nhiều, RSI cao.' },
  TREND_WEAKENING:   { color: colors.accent.orange, Icon: Warning,      label: 'WEAKENING',      short: 'Trend đang yếu dần',   bucket: 'review', tip: 'Momentum giảm, RS yếu hơn thị trường.' },
  BROKEN_TREND:      { color: colors.error.main,               Icon: HighlightOff, label: 'BROKEN',         short: 'Trend đã vỡ',          bucket: 'review', tip: 'Đã mất MA50 hoặc gãy cấu trúc.' },
  NONE:              { color: colors.gray[400],                Icon: null,         label: '—',              short: '',                     bucket: 'none',   tip: '' },
};
export const getStyle = (s) => tradeStyleMeta[s] || tradeStyleMeta.NONE;

export const bucketMeta = {
  buy:    { title: 'Vào lệnh',         color: status.positive.main, bg: status.positive.bg, border: status.positive.border, Icon: CheckCircle,   cta: 'Mua thăm dò / vào lệnh',  sub: 'Backtest WATCHLIST∩LEADER: WR 58%, Avg +9%/40 phiên (alpha +10.9pp)' },
  watch:  { title: 'Theo dõi',         color: status.warning.main,  bg: status.warning.bg,  border: status.warning.border,  Icon: InfoOutlined,  cta: 'Sẵn sàng, chờ tín hiệu',  sub: 'Cấu trúc tốt nhưng chưa đủ trigger — đợi xác nhận' },
  review: { title: 'Rà soát danh mục', color: status.negative.main, bg: status.negative.bg, border: status.negative.border, Icon: ReportProblem, cta: 'Xem lại stop / chốt lời',  sub: 'Late-stage / yếu dần / vỡ trend — cảnh báo holding' },
};

export const failLabel = {
  ext: 'Extension', resil: 'Resilience', risk: 'Risk', rs: 'RS', strength: 'Strength',
};
export const getFailLabel = (key, t) =>
  t ? t(`stockStrength.fails.${key}`, failLabel[key] || key) : (failLabel[key] || key);

export const getFailList = (item) =>
  item?.criteria_fails_list
  || (item?.criteria_fails ? item.criteria_fails.split(',').filter(Boolean) : []);
